<template>
  <form @submit="onSubmit">
    <div class="flex flex-col lg:flex-row gap-x-4 gap-y-2 p-2">
      <div class="flex flex-col gap-2 w-full lg:w-1/2">
        <StackedLabelInput name="companyPhone" label="Company Phone" :max-length="15" />
        <div class="flex gap-2">
          <StackedLabelInput v-slot="slotProps" name="extension" label="Ext." size="w-full md:w-1/3">
            <div>
              <InputGroup class="h-10">
                <InputGroupAddon>+</InputGroupAddon>
                <InputText name="extension" id="extension" type="number" v-model="value" @update:model-value="console.log(slotProps.errorMessage)" />
              </InputGroup>
            </div>
            <small v-if="slotProps.errorMessage" id="extension-help" class="text-danger-500">{{ slotProps.errorMessage }}</small>
          </StackedLabelInput>
          <StackedLabelInput name="phone" label="Phone" size="w-full md:w-2/3" :max-length="15" />
        </div>
        <StackedLabelInput name="email" label="E-mail" />
        <StackedLabelInput name="fax" label="Fax" />
      </div>
      <div class="flex flex-col gap-2 w-full lg:w-1/2">
        <StackedLabelInput name="addressLine1" label="Address Line 1" />
        <StackedLabelInput name="addressLine2" label="Address Line 2" />
        <StackedLabelInput name="city" label="City" />
        <div class="flex gap-2">
          <StateSelect name="state" label="State" size="w-1/2" />
          <StackedLabelInput name="zip" label="Zip Code" size="w-1/2" />
        </div>
      </div>
    </div>
    <slot name="navigation"> </slot>
  </form>
</template>

<script setup lang="ts">
import { useField, useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import { type Entity } from "@/types/Entity";
import { z } from "zod";

const emit = defineEmits<{
  submitStep: [values: any];
}>();

interface Props {
  entity?: Entity;
  data: any;
  setValues?: boolean;
  mode: "Add" | "Edit" | "Copy";
}
const props = withDefaults(defineProps<Props>(), {
  setValues: false,
});

const initialValues: any = {};

if (props.entity) {
  initialValues.extension = props.entity.extension.replace("+", "");
  initialValues.phone = props.entity.entityPhone;
  initialValues.fax = props.entity.fax;
  initialValues.email = props.entity.email;
  initialValues.companyPhone = props.entity.companyPhone;
  initialValues.addressLine1 = props.entity.addressLine1;
  initialValues.addressLine2 = props.entity.addressLine2;
  initialValues.city = props.entity.city;
  initialValues.state = props.entity.state == null ? "" : props.entity.state;
  initialValues.zip = props.entity.zip;
}

const { handleSubmit, setFieldValue } = useForm({
  validationSchema: toTypedSchema(
    z
      .object({
        extension: z.string().max(9).default(""),
        phone: z.string().max(15).default(""),
        companyPhone: z.string().max(15).default(""),
        fax: z.string().default(""),
        email: z.string().email({ message: "Please enter a valid e-mail" }).or(z.literal("")).default(""),
        addressLine1: z.string().default(""),
        addressLine2: z.string().default(""),
        city: z.string().default(""),
        state: z.string().default(""),
        zip: z.string().default(""),
      })
      .refine((data) => !props.data.webAccountEnabled || data.email.length > 0, {
        message: "An email is required while web enabled",
        path: ["email"],
      })
  ),
  initialValues: initialValues,
  keepValuesOnUnmount: true,
});

if (props.setValues) {
  setFieldValue("extension", props.data.extension);
  setFieldValue("phone", props.data.phone);
  setFieldValue("companyPhone", props.data.companyPhone);
  setFieldValue("fax", props.data.fax);
  setFieldValue("email", props.data.email);
  setFieldValue("addressLine1", props.data.addressLine1);
  setFieldValue("addressLine2", props.data.addressLine2);
  setFieldValue("city", props.data.city);
  setFieldValue("state", props.data.state);
  setFieldValue("zip", props.data.zip);
}

const onSubmit = handleSubmit((values: any): void => {
  emit("submitStep", values);
});

const { value } = useField<string>(() => "extension");
</script>
